import axios, { AxiosError } from 'axios';
import {
  API_REQUEST_TIME_OUT,
  NETWORK_ERROR,
  TIME_OUT_EXCEED,
} from "../appConstants/AppConstant";
import type {
  RootApiResponseExtends,
  IBaseUrl,
  RootApiResponseWithData,
} from '../interfaces/BaseApiResponse';
import { useTokenStore } from '../storage/tokenState';
import { isTokenValid, refreshTokenDuplicate } from '../services/AuthService';
import { REFRESH_TOKEN } from '../appConstants/APIEndpoint';
// import { appPackageName, HOSTNAME, version } from '@env';
import { isError, pick } from 'lodash';
import { getDeviceId } from '../utils/helpers/deviceId';
import { getZupeeEnvHeaders } from '../utils/zupeeEnv';

const deviceID = getDeviceId();

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_HOST_NAME}`,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    version: `${process.env.REACT_APP_VERSION}`,
    appversion: `${process.env.REACT_APP_VERSION}`,
    Platform: 'android',
    appname: `${process.env.REACT_APP_APP_NAME}`,
    language: localStorage.getItem("language") || "en",
    'Package-Name': `${process.env.REACT_APP_APP_NAME}`,
    isPwa: true,
    deviceID,
    // CSE / z-stage routing. Both omitted unless the env vars are set.
    ...getZupeeEnvHeaders(),
    // Last, so an explicitly enabled pre-prod session overrides build config.
    ...(sessionStorage.getItem("ppEnabled") && {
    'x-zupee-env': 'pre-prod',
    'x-zupee-pp-token': sessionStorage.getItem("googleToken") || "",
  })
  },
  timeout: API_REQUEST_TIME_OUT,
});

export const setAuthTokenInHeader = (
  accessToken: string,
  identity?: string,
) => {
  axiosInstance.defaults.headers.authorization = accessToken;
  const language = localStorage.getItem("language") || "en";
  if (language) {
    axiosInstance.defaults.headers.language = language;
  }
  if (identity) {
    axiosInstance.defaults.headers.ludoUserId = identity;
  }
};

export const updateLanguageInHeader = () => {
  const language = localStorage.getItem("language") || "en";
  if (language) {
    axiosInstance.defaults.headers.language = language;
  }
};

export function getAuthTokenHeader() {
  return pick(axiosInstance.defaults.headers, [
    'Platform',
    'appname',
    'version',
    'appversion',
    'language',
    'Package-Name',
    'appnameios',
    'authorization',
    'ludoUserId',
  ]);
}

export const getBaseUrl = () => axiosInstance.defaults.baseURL;

// Request interceptor
axiosInstance.interceptors.request.use(
  config => {
    // Add any custom logic here, e.g., authentication tokens
    // config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => {
    // Handle request errors
    return Promise.reject(error);
  },
);

// Response interceptor
axiosInstance.interceptors.response.use(
  response => {
    // Handle successful responses
    return response;
  },
  async error => {
    // Handle response errors
    const isRefreshUrl = error?.config?.url?.includes(REFRESH_TOKEN);

    // A 401 on a request that was never authenticated is not an expired
    // session, it is a request made while logged out. Refreshing from there
    // ends in logout(), which clears storage and hard-navigates to the splash,
    // and the next boot fires the same request again -- the KYC completion
    // retry looped on exactly this.
    const { token } = useTokenStore.getState();
    const hasSession = Boolean(token?.accessToken) && Boolean(token?.refreshToken);

    if (
      error.response &&
      error.response.status === 401 &&
      !isRefreshUrl &&
      hasSession
    ) {
      try {
        await refreshTokenDuplicate();
        const updatedConfig = { ...error?.config };
        updatedConfig.headers = {
          ...updatedConfig.headers,
          authorization: axiosInstance.defaults.headers.authorization,
        };
        return axiosInstance.request(updatedConfig);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // If it's not a 401 error, or it's the refresh URL, reject the original error
    return Promise.reject(error);
  },
);

export async function refreshTokenAPI(urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {}) {

  try {
    const res = await axiosInstance.post(
      urlConfig.url,
      requestData,
      requestHeaders,
    );
    const responseData = res?.data;
    // logger(TAG_NAMES.API_LOGS, `API Success::[${urlConfig.url}]`, {
    //   data: responseData,
    // });
    return responseData;
  } catch (error: any) {
    logApiError(error, 'refreshTokenAPI', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}

// Helper functions
export async function requestApiGet<T>(
  urlConfig: IBaseUrl,
  config = {},
): Promise<RootApiResponseWithData<T>> {

  try {
    const res = await axiosInstance.get(urlConfig.url, config);
    const responseData = res?.data;
    // logger(TAG_NAMES.API_LOGS, `API Success:[${urlConfig.url}]`, {
    //   data: responseData,
    // });
    return responseData;
  } catch (error: any) {
    logApiError(error, 'requestApiGet', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}

export async function requestApiPost<T>(
  urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {},
): Promise<RootApiResponseWithData<T>> {
  // if (!checkInternet()?.isInternetWorking) {
  //   return Promise.reject('Internet Not Reachable');
  // }
  // if (!isTokenValid()) {
  //   await refreshTokenDuplicate();
  // }
  try {
    const res = await axiosInstance.post(
      urlConfig.url,
      requestData,
      requestHeaders,
    );
    const responseData = res?.data;
    // logger(TAG_NAMES.API_LOGS, `API Success::[${urlConfig.url}]`, {
    //   data: responseData,
    // });
    return responseData;
  } catch (error: any) {
    logApiError(error, 'requestApiPost', urlConfig);
    return { en: urlConfig.en, response: { success: false } };
  }
}

export async function requestApiPostTwo<T>(
  urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {},
): Promise<RootApiResponseExtends<T>> {
  // if (!checkInternet()?.isInternetWorking) {
  //   return Promise.reject('Internet Not Reachable');
  // }
  // if (!isTokenValid()) {
  //   await refreshTokenDuplicate();
  // }
  try {
    const res = await axiosInstance.post(
      urlConfig.url,
      requestData,
      requestHeaders,
    );
    const responseData = res?.data;
    // logger(TAG_NAMES.API_LOGS, `API Success for URL: ${urlConfig.url}`, {
    //   data: responseData,
    // });
    return responseData;
  } catch (error: any) {
    logApiError(error, 'requestApiPostTwo', urlConfig);
    return {
      en: urlConfig.en,
      response: { success: false, error: error.message || 'An error occurred', } as unknown as Partial<T>,
    } as RootApiResponseExtends<T>;
  }
}

/**
 * v2 auth transport, mirroring verticaltv-pwa's clean-header post
 * (verticaltv-pwa/src/api/index.ts:196-220).
 *
 * Headers come from `defaults.headers.common` only — NOT the instance defaults
 * — so the PWA markers set in `axios.create` (`isPwa`, `appname`,
 * `Package-Name`, `Platform`, `version`, `appversion`) are deliberately left
 * off. Those are what make the backend apply its "existing full-app user may
 * not sign up on the PWA" rule (errorCode 1013).
 *
 * Same signature, return shape and error handling as `requestApiPostTwo`, so
 * callers can swap between them freely.
 */
export async function requestAuthApiV2<T>(
  urlConfig: IBaseUrl,
  requestData = {},
  requestHeaders = {},
): Promise<RootApiResponseExtends<T>> {
  try {
    const baseURL = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
    const apiPath = urlConfig.url.startsWith('/') ? urlConfig.url : `/${urlConfig.url}`;
    const fullUrl = urlConfig.url.startsWith('http') ? urlConfig.url : `${baseURL}${apiPath}`;

    const commonHeaders = axiosInstance.defaults.headers.common || {};
    const res = await axios.post(fullUrl, requestData, {
      headers: {
        ...commonHeaders,
        'Content-Type': 'application/json',
        'x-zupee-source': 'web',
        // `commonHeaders` is only `defaults.headers.common`, so the instance
        // defaults set in axios.create are not inherited here.
        ...getZupeeEnvHeaders(),
        ...requestHeaders,
      },
      timeout: API_REQUEST_TIME_OUT,
    });
    return res?.data;
  } catch (error: any) {
    logApiError(error, 'requestAuthApiV2', urlConfig);
    return {
      en: urlConfig.en,
      response: { success: false, error: error.message || 'An error occurred', } as unknown as Partial<T>,
    } as RootApiResponseExtends<T>;
  }
}

function logApiError(error: Error | AxiosError, functionName: string, urlConfig: IBaseUrl) {
  if (
    !(
      error?.message === NETWORK_ERROR ||
      error?.message?.includes(TIME_OUT_EXCEED)
    )
  ) {
    // logger(TAG_NAMES.GENERIC, `API Failed::[${functionName}]`, {
    //   isBreadcrumb: true,
    //   error,
    //   data: urlConfig,
    // });
  } else if (axios.isAxiosError(error)) {
    // logger(TAG_NAMES.API_LOGS, 'API Failed::[Axios]', {
    //   data: { message: (error as AxiosError).message, code: (error as AxiosError).code as string, urlConfig: urlConfig },
    // });
  } else if (isError(error)) {
    // logger(TAG_NAMES.API_LOGS, 'API Failed::[JS Error]', {
    //   data: { message: error.message, stack: error.stack as string, urlConfig: urlConfig },
    // });
  }
}
